/**
 * Sentry Integration for Frontend
 * 
 * Features:
 * - Error tracking with React Error Boundaries
 * - Performance monitoring
 * - User identification (safe - no PII)
 * - Request tracing via request_id from backend
 */
import * as Sentry from "@sentry/react";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || "";
const SENTRY_ENABLED = !!SENTRY_DSN;
const ENVIRONMENT = process.env.NODE_ENV || "development";

// Drop events from non-production hosts (preview/staging/localhost) so we
// don't spam Slack/email during dev work. Production = our real domains.
const PRODUCTION_HOSTS = [
  "millennialsportal.com",
  "www.millennialsportal.com",
  "staging.millennialsportal.com",  // staging still counted as prod for alerts
];
const isProductionHost = () => {
  if (typeof window === "undefined") return false;
  return PRODUCTION_HOSTS.includes(window.location.hostname);
};

/**
 * Initialize Sentry. Call once at application startup.
 */
export function initSentry() {
  if (!SENTRY_ENABLED) {
    console.log("Sentry disabled (no REACT_APP_SENTRY_DSN configured)");
    return false;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      
      // Performance monitoring (10% of transactions)
      tracesSampleRate: 0.1,
      
      // Replay for debugging (1% of sessions, 100% on error)
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 1.0,
      
      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask all text to protect PII
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      
      // Before sending, strip sensitive data
      beforeSend(event) {
        // ── DROP preview/dev errors at the source — keeps Slack clean ──
        if (!isProductionHost()) {
          return null;
        }
        // Remove any accidental PII
        if (event.request?.data) {
          const data = event.request.data;
          ["password", "email", "token", "api_key"].forEach(key => {
            if (data[key]) data[key] = "[REDACTED]";
          });
        }
        return event;
      },
      
      // Don't send errors from extensions or third-party scripts
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "Non-Error promise rejection",
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
      ],
    });

    console.log(`Sentry initialized (env: ${ENVIRONMENT})`);
    return true;
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
    return false;
  }
}

/**
 * Set user context (safe info only - no email/PII)
 */
export function setSentryUser(userId, role, tenantId = null) {
  if (!SENTRY_ENABLED) return;
  
  Sentry.setUser({
    id: userId,
    role: role,
    tenant_id: tenantId || "system",
  });
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  if (!SENTRY_ENABLED) return;
  Sentry.setUser(null);
}

/**
 * Add request_id tag (from backend response headers)
 */
export function setRequestId(requestId) {
  if (!SENTRY_ENABLED || !requestId) return;
  Sentry.setTag("request_id", requestId);
}

/**
 * Capture exception manually
 */
export function captureException(error, extra = {}) {
  if (!SENTRY_ENABLED) {
    console.error("Error (Sentry disabled):", error);
    return;
  }
  
  Sentry.withScope(scope => {
    Object.entries(extra).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

/**
 * Capture message/event
 */
export function captureMessage(message, level = "info", extra = {}) {
  if (!SENTRY_ENABLED) {
    console.log(`[${level}] ${message}`);
    return;
  }
  
  Sentry.withScope(scope => {
    Object.entries(extra).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureMessage(message, level);
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message, category = "custom", data = {}) {
  if (!SENTRY_ENABLED) return;
  
  Sentry.addBreadcrumb({
    message,
    category,
    data,
  });
}

// Export Sentry's ErrorBoundary for use in components
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Export status
export { SENTRY_ENABLED };
